export interface Project {
  id: string
  name: string
  description?: string
  path: string
  url: string
  info?: any
  data?: any // Add data property to store the Info.json content
}

export interface ProjectData {
  Lines: any
  [key: string]: any
}

const projectService = {
  async getProjectById(projectId: string, apiEndpoint: string): Promise<Project | null> {
    try {
      // Fetch the list of assets from the API
      const response = await fetch(`${apiEndpoint}assets`)

      if (!response.ok) {
        throw new Error(`Failed to fetch assets: ${response.statusText}`)
      }

      const data = await response.json()

      if (!data.assets || !Array.isArray(data.assets)) {
        throw new Error("API returned invalid data format")
      }

      // Find the project with the matching ID (which is the name in this case)
      const project = data.assets.find((asset: any) => asset.name === projectId)

      if (!project) {
        return null
      }

      return {
        id: project.name,
        name: project.name,
        description: `Asset from ${apiEndpoint}`,
        path: project.path,
        url: project.url,
        info: project.info,
      }
    } catch (error: any) {
      console.error("Error fetching project:", error)
      throw new Error(`Failed to fetch project: ${error.message}`)
    }
  },

  async fetchProjectData(project: Project, apiEndpoint: string): Promise<ProjectData | null> {
    try {
      // First, get the list of files and directories for this project
      const response = await fetch(project.url)

      if (!response.ok) {
        throw new Error(`Failed to fetch project contents: ${response.statusText}`)
      }

      const directoryData = await response.json()

      // Find the Info.json file in the files list
      const infoFile = directoryData.files?.find((file: any) => file.name === "Info.json")

      if (!infoFile) {
        throw new Error("Info.json file not found in project")
      }

      // Fetch the Info.json file content
      const infoResponse = await fetch(infoFile.url)

      if (!infoResponse.ok) {
        throw new Error(`Failed to fetch Info.json: ${infoResponse.statusText}`)
      }

      const infoData = await infoResponse.json()

      if (!infoData || !infoData.Lines) {
        throw new Error("Invalid project data format - missing Lines property")
      }

      return infoData as ProjectData
    } catch (error: any) {
      console.error("Error fetching project data:", error)
      return null
    }
  },

  async getProjects(apiEndpoint: string): Promise<Project[]> {
    try {
      // Fetch the list of assets from the API
      const response = await fetch(`${apiEndpoint}assets`)

      if (!response.ok) {
        throw new Error(`Failed to fetch assets: ${response.statusText}`)
      }

      const data = await response.json()

      if (!data.assets || !Array.isArray(data.assets)) {
        throw new Error("API returned invalid data format")
      }

      // Map the assets to our Project interface
      return data.assets.map((asset: any) => ({
        id: asset.name,
        name: asset.name,
        description: asset.info?.description || `Asset from ${apiEndpoint}`,
        path: asset.path,
        url: asset.url,
        info: asset.info,
      }))
    } catch (error: any) {
      console.error("Error fetching projects:", error)
      throw new Error(`Failed to fetch projects: ${error.message}`)
    }
  },

  async getProjectWithData(project: Project, apiEndpoint: string): Promise<Project | null> {
    try {
      // First, get the list of files and directories for this project
      const response = await fetch(project.url)

      if (!response.ok) {
        throw new Error(`Failed to fetch project contents: ${response.statusText}`)
      }

      const directoryData = await response.json()

      // Find the Info.json file in the files list
      const infoFile = directoryData.files?.find((file: any) => file.name === "Info.json")

      if (!infoFile) {
        throw new Error("Info.json file not found in project")
      }

      // Fetch the Info.json file content
      const infoResponse = await fetch(infoFile.url)

      if (!infoResponse.ok) {
        throw new Error(`Failed to fetch Info.json: ${infoResponse.statusText}`)
      }

      const infoData = await infoResponse.json()

      // Return the project with the data
      return {
        ...project,
        data: infoData,
      }
    } catch (error: any) {
      console.error("Error fetching project with data:", error)
      return null
    }
  },
}

export default projectService

